::selection {
  background-color: transparent;
}

/* Read-only deletion text sits outside the editor's selection overlay, so the
   global transparent ::selection above hides its selection by default. Reveal
   the native selection (so users can see what they select and copy) only while
   the user is actively selecting within deleted text: the editor sets
   data-deleted-text-selection on the diff while a pointer gesture starts there.
   Gating it this way means a normal selection of editable code that merely
   sweeps across an interleaved deleted line (unified view) does not highlight
   that line. Covers the whole deletions column in split view and individual
   deleted lines in unified view. */
[data-deleted-text-selection] [data-deletions] ::selection,
[data-deleted-text-selection] [data-line-type='change-deletion'] ::selection {
  background-color: var(--diffs-editor-selection-bg);
}

@keyframes blinking {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

:host, /* for scroll anchor */
[data-code], /* for editor overlay */
[data-content] /* for wrap line measurement */,
[data-diff-type='split'][data-overflow='wrap'] {
  position: relative;
}
[data-code] {
  container-type: inline-size;
}
[data-content] {
  cursor: text;
  background-color: transparent;
  caret-color: var(--diffs-bg-caret);
  outline: none;
}
[data-line] {
  cursor: text;
  /* Transparent so the line's in-flow background can't hide the selection
     overlay (z-index -10); the line color moves to the ::after layer below it. */
  background-color: transparent;
}
/* Line background layer below the selection overlay (z-index -10) so the
   translucent selection composites over it. ::before is the +/- indicator. */
[data-line]::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -20;
  background-color: var(--diffs-line-bg);
  pointer-events: none;
}
/* The line-state pipeline resolves the active background. Keep the theme's
   border as an additional cue, including for border-only themes. */
[data-line][data-editor-active-line]::after {
  box-shadow: inset 0 0 0 1px var(--diffs-editor-line-highlight-border);
}

[data-editor-overlay] {
  display: contents;
}
[data-caret],
[data-edit-prediction],
[data-selection-range],
[data-match-range],
[data-bracket-match-range],
[data-edit-prediction-deletion-range],
[data-edit-prediction-insertion-range],
[data-edit-prediction-replacement-range],
[data-marker-range],
[data-remote-caret],
[data-caret-highlight-range] {
  position: absolute;
  top: 0;
  left: 0;
  height: 1lh;
  line-height: var(--diffs-line-height);
  pointer-events: none;
}
[data-edit-prediction] {
  z-index: 1;
  height: auto;
  overflow: visible;
  width: max-content;
  user-select: none;
  color: var(
    --diffs-editor-edit-prediction-fg,
    color-mix(in lab, var(--diffs-fg) 45%, transparent)
  );
}
[data-edit-prediction-line] {
  box-sizing: border-box;
  display: block;
  min-height: 1lh;
  width: max-content;
  white-space: pre;
}
[data-edit-prediction][data-replacement] [data-edit-prediction-line],
[data-edit-prediction-insertion-range],
[data-edit-prediction-replacement-range] {
  background-color: var(--diffs-edit-prediction-bg, var(--diffs-bg));
}
[data-edit-prediction-suffix] {
  color: var(--diffs-fg);
}
[data-edit-prediction][data-wrap] [data-edit-prediction-line] {
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
[data-edit-prediction-line][data-empty]::after {
  content: '↵';
}
[data-edit-prediction-spacer] {
  margin-block-end: var(--diffs-edit-prediction-spacer-height);
}
/* The deletions column of a split diff shows no ghost text, so its reserved
   rows are padding: the row tint and the buffer hatch extend over them instead
   of leaving a blank strip. Buffers size themselves with a border-box
   min-height that would absorb the padding, hence content-box. */
[data-code][data-deletions] [data-edit-prediction-spacer] {
  margin-block-end: 0;
  padding-block-end: var(--diffs-edit-prediction-spacer-height);
  box-sizing: content-box;
}
[data-edit-prediction-deletion-range] {
  --diffs-edit-prediction-deletion-color: var(
    --diffs-editor-edit-prediction-deletion-fg,
    color-mix(in lab, var(--diffs-deletion-base) 70%, transparent)
  );

  z-index: 1;
  background: linear-gradient(
    to bottom,
    transparent calc(50% - 0.5px),
    var(--diffs-edit-prediction-deletion-color) calc(50% - 0.5px),
    var(--diffs-edit-prediction-deletion-color) calc(50% + 0.5px),
    transparent calc(50% + 0.5px)
  );
}
[data-edit-prediction-insertion-range] {
  z-index: 0;
}
[data-edit-prediction-replacement-range] {
  z-index: 1;
}
[data-caret] {
  z-index: 2;
  width: 2px;
  background-color: var(
    --diffs-bg-caret-override,
    var(
      --diffs-editor-cursor-fg,
      light-dark(
        color-mix(in lab, var(--diffs-fg) 50%, var(--diffs-bg)),
        color-mix(in lab, var(--diffs-fg) 75%, var(--diffs-bg))
      )
    )
  );
  visibility: hidden;
}
/* Reduce motion only if user has set a preference */
@media (prefers-reduced-motion: no-preference) {
  [data-caret] {
    animation: blinking 1.2s infinite;
    animation-delay: 0.8s;
  }
}
[data-selection-range] {
  z-index: -10;
  background-color: var(--diffs-editor-selection-bg);
}
[data-caret-highlight-range] {
  z-index: -10;
  background-color: var(
    --diffs-caret-highlight-bg,
    var(--diffs-editor-selection-bg)
  );
}
/* Masks the selection's square corner to fake rounding, so it must match the bg
   behind the selection: the line's diff color (passed in via
   --diffs-selection-corner-bg) or the editor base for context lines. */
[data-selection-corner] {
  width: 100%;
  height: 100%;
  background-color: var(--diffs-selection-corner-bg, var(--diffs-bg));
}
[data-match-range] {
  z-index: -10;
  background-color: var(
    --diffs-editor-match-bg,
    var(--diffs-editor-selection-bg)
  );
}
[data-match-range]:not([data-focus]) {
  background-color: var(
    --diffs-editor-match-highlight-bg,
    light-dark(#ff963288, #ff963266)
  );
}
[data-bracket-match-range] {
  z-index: -9;
  box-sizing: border-box;
  border-bottom: 1px solid
    var(
      --diffs-editor-bracket-match-border,
      color-mix(in lab, var(--diffs-fg) 60%, transparent)
    );
  background-color: var(
    --diffs-editor-bracket-match-bg,
    color-mix(in lab, var(--diffs-fg) 5%, transparent)
  );
}

[data-rtl] {
  border-top-left-radius: 3px;
}
[data-rtr] {
  border-top-right-radius: 3px;
}
[data-rbl] {
  border-bottom-left-radius: 3px;
}
[data-rbr] {
  border-bottom-right-radius: 3px;
}

/* Markers
 *
 * Marker range applies a wavy underline. Severity attributes are used by both
 * `[data-marker-range]` and `[data-marker-popover]` alike.
 */

[data-marker-range] {
  z-index: 1;
  background-color: var(--diffs-marker-bg);
  mask-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2IiBoZWlnaHQ9IjMiPjxwYXRoIGQ9Im0wIDIuNSBsMiAtMS41IGwxIDAgbDIgMS41IGwxIDAiIHN0cm9rZT0iI2ZmZiIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9zdmc+');
  mask-position: left bottom;
  mask-repeat: repeat-x;
  mask-size: 6px 3px;
}

[data-marker-error] {
  --diffs-marker-contrast: light-dark(#fff, #000);
  --diffs-marker-bg: var(--diffs-editor-error-fg, var(--diffs-deletion-base));
}
[data-marker-warning] {
  --diffs-marker-contrast: #000;
  --diffs-marker-bg: var(
    --diffs-editor-warning-fg,
    light-dark(var(--diffs-warning-light), var(--diffs-warning-dark))
  );
}
[data-marker-info] {
  --diffs-marker-contrast: #000;
  --diffs-marker-bg: var(--diffs-editor-info-fg, var(--diffs-modified-base));
}
[data-marker-hint] {
  --diffs-marker-contrast: light-dark(#fff, #000);
  --diffs-marker-bg: var(--diffs-editor-hint-fg, var(--diffs-fg-number));
}

@media (width >= 480px) {
  [data-content] {
    caret-color: transparent;
  }
  [data-content]:focus ~ [data-editor-overlay] [data-caret] {
    visibility: visible;
  }
}

/* Editor widgets
 *
 * Positioned popovers used across edit mode. Used by markers, search panel,
 * and selection action.
 */

[data-editor-widget] {
  --diffs-widget-fg: var(--diffs-fg);
  --diffs-widget-bg: color-mix(in lab, var(--diffs-fg) 4%, var(--diffs-bg));
  --diffs-widget-border: color-mix(in lab, var(--diffs-fg) 20%, transparent);
  --diffs-widget-shadow:
    inset 0 0 0 1px var(--diffs-bg), 0 4px 8px rgb(0 0 0 / 0.075),
    0 6px 18px rgb(0 0 0 / 0.075);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  width: fit-content;
  max-width: calc(100% - 24px);
  font: 14px/20px var(--diffs-header-font-fallback);
  border: 1px solid var(--diffs-widget-border);
  border-radius: 9px;
  background-color: var(--diffs-widget-bg);
  background-clip: padding-box;
  box-shadow: var(--diffs-widget-shadow);
}

/* Shared geometry for the overlay popovers (marker hover popover and
   selection-action popover). Both are positioned in overlay coordinate space via
   --popover-x/--popover-y (set in JS). The left edge is clamped so the popover
   never slides under the line-number gutter (--gutter-width, also set in JS) or
   outside the visible scroll viewport, and the width is capped to match. Builds
   on [data-editor-widget]. */
[data-marker-popover],
[data-selection-action-popover] {
  --popover-gap: var(--diffs-popover-gap, var(--diffs-gap-fallback, 8px));
  --popover-viewport-left: 0px;
  --popover-viewport-right: 100cqw;
  /* Sentinels disable the vertical clamp until JS sets the real scrollport. */
  --popover-viewport-top: -99999px;
  --popover-viewport-bottom: 99999px;
  --popover-min-left: calc(
    var(--popover-viewport-left) + var(--gutter-width, 0px) + var(--popover-gap)
  );
  --popover-available-width: max(
    0px,
    calc(
      var(--popover-viewport-right) - var(--popover-min-left) -
        var(--popover-gap)
    )
  );
  max-width: min(640px, var(--popover-available-width));
  translate: clamp(
      var(--popover-min-left),
      var(--popover-x, var(--popover-min-left)),
      calc(var(--popover-viewport-right) - var(--popover-gap) - 100%)
    )
    /* Clamp into the scrollport so a popover with no fitting side stays in view. */
    clamp(
      var(--popover-viewport-top),
      calc(var(--popover-y, 0px) + var(--popover-y-shift, 0px)),
      calc(var(--popover-viewport-bottom) - var(--popover-gap) - 100%)
    );
}

[data-marker-popover] {
  --diffs-widget-bg: var(--diffs-marker-bg);
  --diffs-widget-shadow:
    0 4px 8px rgb(0 0 0 / 0.15), 0 6px 18px rgb(0 0 0 / 0.15);

  padding: 8px 12px;
  min-width: min(180px, var(--popover-available-width));
  overflow-wrap: anywhere;
  color: var(--diffs-marker-contrast);
  white-space: normal;
  border: 0;
  pointer-events: auto;

  @supports (color: contrast-color(red)) {
    color: contrast-color(var(--diffs-marker-bg, var(--diffs-bg)));
  }

  [data-marker-message] {
    white-space: normal;
    overflow-wrap: anywhere;

    code {
      display: inline;
      background-color: transparent;
      white-space: normal;
      color: inherit;
    }
  }
}

/* Selection Action Widget: a floating popover hosting the consumer's
   selection-action element. It reuses the shared [data-editor-widget] chrome and
   sits in the overlay layer (which is pointer-events: none), so it re-enables
   pointer events for its own actions. */
[data-selection-action-popover] {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  width: max-content;
  padding: 4px;
  pointer-events: auto;
}

/* Search Panel Widget */
[data-search-panel] {
  position: sticky;
  top: 16px;
  right: 16px;
  z-index: 100;
  display: flex;
  justify-content: right;
  flex-direction: column;
  width: 100%;
  /* Zero height ensures panel appears stickied immediately */
  height: 0;
  container: search-panel / inline-size;

  [data-editor-widget] {
    position: relative;
    z-index: 100;
    display: flex;
    flex-shrink: 0;
    align-items: stretch;
    gap: 8px;
    padding: 4px;
    max-width: 100%;
    min-width: 260px;
    margin-inline: auto 16px;
  }

  svg {
    display: block;
    fill: currentColor;
    width: 12px;
    height: 12px;
  }
}

[data-search-grid] {
  display: grid;
  grid-template-columns: auto auto auto;
  grid-template-areas:
    'find    matches nav'
    'replace actions .';
  align-items: center;
  gap: 4px 6px;
  width: 100%;
}
[data-input-box][data-find] {
  grid-area: find;
}
[data-search-nav] {
  grid-area: nav;
}
[data-input-box][data-replace] {
  grid-area: replace;
}
[data-replace-actions] {
  grid-area: actions;
}
[data-search-grid][data-mode='find'] {
  grid-template-areas: 'find matches nav';
}
[data-search-grid][data-mode='find'] [data-replace-cell] {
  display: none;
}

@container search-panel (width < 400px) {
  [data-search-panel] [data-editor-widget] {
    padding: 10px;
  }
  [data-search-grid][data-mode='replace'] {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'find    find    find'
      'replace replace replace'
      'nav     matches actions';
  }
  [data-search-grid][data-mode='find'] {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'find find    find'
      'nav  matches matches';
  }
  [data-replace-actions] {
    justify-self: end;
  }
  [data-search-grid] [data-input-box] {
    width: auto;
  }
}

[data-input-box] {
  position: relative;
  display: flex;
  align-items: center;
  width: 200px;

  input {
    flex-grow: 1;
    width: 100%;
    min-width: 0;
    font-size: 13px;
    line-height: 24px;
    padding-inline: 6px;
    color: var(--diffs-fg);
    background-color: var(--diffs-bg);
    border: 1px solid color-mix(in lab, var(--diffs-fg) 12%, var(--diffs-bg));
    border-radius: 6px;
    outline: none;

    &::selection {
      background-color: color-mix(in lab, var(--diffs-fg) 8%, var(--diffs-bg));
    }

    &:focus-visible {
      outline: 1px solid var(--diffs-modified-base);
    }
  }

  &[data-find] input {
    /* Space for overlaid search icon toggles */
    padding-inline-end: 72px;
  }

  [data-search-icon] {
    --diffs-search-icon-size: 20px;
  }
}

[data-search-toggles] {
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 1px;
}

[data-matches] {
  grid-area: matches;
  flex-shrink: 0;
  min-width: 50px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  color: color-mix(in lab, var(--diffs-fg) 50%, var(--diffs-bg));

  &[data-no-matches] {
    color: color-mix(in lab, var(--diffs-fg) 35%, var(--diffs-bg));
  }
}

[data-replace-actions],
[data-search-nav] {
  display: flex;
  align-items: center;
}

/* Every clickable control is a real <button> tagged with data-search-icon;
   strip the UA chrome here so this single rule is the source of their
   appearance. */
[data-search-icon] {
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  flex-shrink: 0;
  width: var(--diffs-search-icon-size, 24px);
  height: var(--diffs-search-icon-size, 24px);
  margin: 0;
  padding: 0;
  color: color-mix(in lab, var(--diffs-fg) 65%, var(--diffs-bg));
  border: 0;
  border-radius: var(--diffs-search-icon-radius, 4px);
  background: none;
  cursor: pointer;
  transition:
    background-color 0.1s ease-in-out,
    color 0.1s ease-in-out;

  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
  &:not(:disabled):hover {
    color: var(--diffs-fg);
  }
  &:focus-visible {
    outline: 1px solid var(--diffs-modified-base);
  }
  &[aria-pressed='true'] {
    color: var(--diffs-modified-base);
  }
}

[data-search-close] {
  --diffs-search-icon-size: 20px;
  --diffs-search-icon-radius: 50%;
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  color: var(--diffs-widget-bg);
  background-color: color-mix(
    in lab,
    var(--diffs-widget-fg) 50%,
    var(--diffs-bg)
  );

  &:not(:disabled):hover {
    opacity: 1;
    color: var(--diffs-widget-bg);
    background-color: var(--diffs-widget-fg);
  }
}
